import React, { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import CssBaseline from "@mui/material/CssBaseline";
import Drawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Typography from "@mui/material/Typography";
import { Link } from "react-router-dom";
import { Avatar, Collapse } from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import AddIcon from "@mui/icons-material/Add";
import { ReactComponent as RemoveIcon } from "../../assets/images/remove.svg";
import { ReactComponent as BlackDot } from "../../assets/images/blackDot.svg";
import { ReactComponent as WhiteDot } from "../../assets/images/whitedot.svg";
import { ReactComponent as Dashboard } from "../../assets/images/dashboard.svg";
import { ReactComponent as BlackDashboard } from "../../assets/images/blackDash.svg";
import { ReactComponent as AccountSetting } from "../../assets/images/settingSmall.svg";
import { ReactComponent as BlackAccountSetting } from "../../assets/images/settingSmallWhite.svg";

import AuthService from "../../api/AuthService";
import { Appimg } from "../../constants/imgfile";
import styles from "./style";
import { accountingModule, hrModule, superAdmin } from "../../constants/pathFile";

const ROLE_ADMIN = "Admin";
const ROLE_SUPERADMIN = "SuperAdmin";
const ROLE_EMPLOYEE = "Employee";
const ROLE_SUBADMIN = "SubAdmin";

const drawWidth = 304;

function Sidebar({ mobileViewOpen, setMobileViewOpen }) {
  const [activeTab, setActiveTab] = useState(() => localStorage.getItem("activeTab") || "Dashboard");
  const [openStates, setOpenStates] = useState(() => {
    const saved = localStorage.getItem("openStates");
    return saved ? JSON.parse(saved) : {};
  });
  const [data, setData] = useState();
  const [pendingProfileChangesCount, setPendingProfileChangesCount] = useState(0);
  const [permission, setPermission] = useState(() => {
    try {
      const stored = localStorage.getItem("permission");
      return stored ? JSON.parse(stored) : null;
    } catch (error) {
      return null;
    }
  });
  const role = localStorage.getItem("role") || "";
  const isEmployee = role === ROLE_EMPLOYEE;
  const isAdminRole = role === ROLE_ADMIN || role === ROLE_SUPERADMIN;
  const hasDashboardEdit = permission?.employee?.isEdit === true;
  const canShowDashboard = !isEmployee || hasDashboardEdit;
  const isControlCenter =
    JSON.parse(localStorage.getItem("isControlCenter") || "false");
    
  // Re-fetch permissions from the API. Used only to refresh permissions when explicitly
  // notified (e.g. after an Admin updates an Employee's permissions) — NOT on initial
  // mount, so the permission returned by login (and stored in localStorage) remains the
  // source of truth for the initial dashboard load.
  const fetchPermissions = async () => {
    const userRole = localStorage.getItem("role") || "";
    const employeeId = localStorage.getItem("employeeId");

    // Only fetch for Employee role
    if (userRole === ROLE_EMPLOYEE && employeeId) {
      try {
        const permissionResult = await AuthService.getEmployeePermission(employeeId);
        if (permissionResult?.success && permissionResult?.data) {
          // API returns permissions directly in data object
          const permissions = permissionResult?.data;
          setPermission(permissions);
          localStorage.setItem("permission", JSON.stringify(permissions));
        }
      } catch (error) {
        console.error("Error fetching permissions:", error);
      }
    } else if (userRole === ROLE_ADMIN || userRole === ROLE_SUPERADMIN) {
      // For Admin/SuperAdmin, set null or empty object (they have all permissions)
      setPermission(null);
    }
  };

  // On mount: Admin/SuperAdmin have their permission cleared synchronously (no API call
  // needed — they bypass permission checks). Employees keep whatever permission was
  // already loaded from localStorage at login; it is only refreshed via the
  // permissionUpdated event, never automatically on mount.
  useEffect(() => {
    const userRole = localStorage.getItem("role") || "";
    if (userRole === ROLE_ADMIN || userRole === ROLE_SUPERADMIN) {
      setPermission(null);
    }

    const handlePermissionUpdate = () => {
      fetchPermissions();
    };

    window.addEventListener("permissionUpdated", handlePermissionUpdate);
    return () => {
      window.removeEventListener("permissionUpdated", handlePermissionUpdate);
    };
  }, []);

  const handleToggle = () => {
    setMobileViewOpen(!mobileViewOpen);
  };

  const getProfileView = async () => {
    try {
      const result = await AuthService.getProfileView();
      if (result?.success) {
        setData(result?.data);
      }
    } catch (error) {
      console.error(error)
    }
  };

  useEffect(() => {
    getProfileView();
  }, []);

  const getPendingProfileChangesCount = async () => {
    try {
      const result = await AuthService.getPendingUpdates();
      if (result?.success) {
        setPendingProfileChangesCount(Array.isArray(result?.data) ? result.data.length : 0);
      }
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    getPendingProfileChangesCount();

    const handleProfileChangesUpdate = () => {
      getPendingProfileChangesCount();
    };

    window.addEventListener("profileChangesUpdated", handleProfileChangesUpdate);
    return () => {
      window.removeEventListener("profileChangesUpdated", handleProfileChangesUpdate);
    };
  }, []);

  useEffect(() => {
    localStorage.setItem("activeTab", activeTab);
  }, [activeTab]);

  useEffect(() => {
    if (isEmployee && !hasDashboardEdit && activeTab === "Dashboard") {
      if (
        permission?.attendance?.isView ||
        permission?.attendance?.isEdit ||
        permission?.attendance?.isEditOwn ||
        permission?.attendance?.isEditDepartment
      ) {
        setActiveTab("Attendance");
      } else {
        setActiveTab("");
      }
    }
  }, [isEmployee, permission, hasDashboardEdit, activeTab]);

  useEffect(() => {
    localStorage.setItem("openStates", JSON.stringify(openStates));
  }, [openStates]);

  const toggleCollapse = (key) => {
    setOpenStates((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const renderModule = (items, parentKey = "") => {
    return items.map((item, index) => {
      const key = parentKey ? `${parentKey}-${index}` : `${index}`;
      const isOpen = openStates[key] || false;

      if (item?.innerModule) {
        return (
          <React.Fragment key={key}>
            <ListItemButton onClick={() => toggleCollapse(key)} sx={{ ...styles.listItemButton, px: 1, paddingRight: 5 }}>
              <ListItemText primary={item.name} sx={isOpen ? styles.activeClr : styles.inactiveClr} />
              {isOpen ? <RemoveIcon /> : <AddIcon />}
            </ListItemButton>
            <Collapse in={isOpen} timeout="auto" unmountOnExit>
              <List component="ul" disablePadding sx={{ borderBottom: "1px solid #DEDEDE" }}>
                {renderModule(item.innerModule, key)}
              </List>
            </Collapse>
          </React.Fragment>
        );
      }

      // Employee platform does not expose the staff directory
      if (isEmployee && item?.name === "Employee List") {
        return null;
      }

      let permissionKey = item?.permission;
      if (item?.permission === "commisson") {
        permissionKey = "commission";
      } else if (item?.permission === "travelAllowances") {
        permissionKey = "travelAllowance";
      }

      const canViewAttendance = permission?.attendance?.isView === true;

      const hasPermission = permission && (
        permissionKey === "reports"
          ? permission[permissionKey]?.isEdit === true
          : permissionKey === "attendance"
            ? canViewAttendance
            : permission[permissionKey]?.isView === true
      );
      const isAdmin = role === ROLE_ADMIN;
      const isSuperAdmin = role === ROLE_SUPERADMIN;
      const noPermissionRequired = !item?.permission;

      if (!(hasPermission || isAdmin || isSuperAdmin || noPermissionRequired)) {
        return null;
      }

      const itemLabel = item.name === "Profile Changes" && pendingProfileChangesCount > 0
        ? `${item.name} (${pendingProfileChangesCount})`
        : item.name;

      return (
        <ListItemButton key={key} component={Link} to={item.to} sx={styles.listItemButton}>
          <Box sx={activeTab === item.name ? styles.button : styles.buttonInactive} onClick={() => setActiveTab(item.name)}>
            {activeTab === item.name ? <WhiteDot /> : <BlackDot />}
            <ListItemText primary={itemLabel} sx={{ ml: 1 }} />
          </Box>
        </ListItemButton>
      );
    });
  };

  const responsiveDrawer = (
    <Box>
      {mobileViewOpen && (
        <Box sx={{ width: "95%", display: "flex", justifyContent: "flex-end" }}>
          <ChevronLeftIcon
            onClick={() => setMobileViewOpen(!mobileViewOpen)}
            style={{
              width: "20px",
              height: "20px",
              borderRadius: "100%",
              marginTop: "10px",
              cursor: "pointer",
              color: "black",
            }}
          />
        </Box>
      )}

      <Box sx={{ ...styles.logo }}>
        <Appimg.Logo />
      </Box>
      <Box sx={{ ...styles.logo, ...styles.align }}>
        <Avatar
          src={data?.personalInfo?.image}
          sx={{ ...styles.profile, '& img': { objectFit: 'cover', width: '100%', height: '100%' }, backgroundColor: '#e0e0e0' }}
        >
          {!data?.personalInfo?.image && <PersonIcon sx={{ fontSize: 50, color: '#757575' }} />}
        </Avatar>
        <Typography sx={{ ...styles.heading }}>
          {data?.personalInfo?.firstName} {data?.personalInfo?.surName}
        </Typography>
        <Typography sx={{ ...styles.subHeading }}>{role}</Typography>
      </Box>

      <List sx={styles.margin}>
        {/* Dashboard */}
        {canShowDashboard && (
          <ListItemButton key="Dashboard" component={Link} to="../dashboard" sx={styles.listItemButton}>
            <Box sx={activeTab === "Dashboard" ? styles.button : styles.buttonInactive} onClick={() => setActiveTab("Dashboard")}>
              {activeTab === "Dashboard" ? <Dashboard /> : <BlackDashboard />}
              <ListItemText primary="Dashboard" sx={{ ml: 1 }} />
            </Box>
          </ListItemButton>
        )}

        {/* HR */}
        {renderModule(hrModule, "hr")}

        {/* Accounting */}
        {/*  {renderModule(accountingModule, "acc")} */}

        {/* Account Settings - Hide for employee role */}
        {!isEmployee && (permission?.account?.isView || isAdminRole) && (
          <ListItemButton key="Account Settings" component={Link} to="./settings" sx={styles.listItemButton}>
            <Box sx={activeTab === "Account Settings" ? styles.button : styles.buttonInactive} onClick={() => setActiveTab("Account Settings")}>
              {activeTab === "Account Settings" ? <BlackAccountSetting /> : <AccountSetting />}
              <ListItemText primary="Account Settings" sx={{ ml: 1 }} />
            </Box>
          </ListItemButton>
        )}

        {/* Super Admin - Only show for SuperAdmin role */}
        {(role === ROLE_SUPERADMIN || role === ROLE_SUBADMIN) &&
          renderModule(
            role === ROLE_SUBADMIN
              ? superAdmin.map(section => ({
                ...section,
                innerModule: section.innerModule.filter((item) => {
                  if (item.name === "Sub Admin") return false;

                  if (item.name === "Control Centre" && !isControlCenter) {
                    return false;
                  }

                  return true;
                }),
              }))
              : superAdmin,
            "sa"
          )}
      </List>
    </Box>
  );

  return (
    <Box sx={styles.main}>
      <CssBaseline />
      <Box component="nav" sx={{ width: { sm: drawWidth }, flexShrink: { sm: 0 } }}>
        <Drawer
          variant="temporary"
          open={mobileViewOpen}
          onClose={handleToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: "block", sm: "none" },
            "& .MuiDrawer-paper": { boxSizing: "border-box", width: drawWidth },
          }}
        >
          {responsiveDrawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: "none", sm: "block" },
            "& .MuiDrawer-paper": { boxSizing: "border-box", width: drawWidth },
          }}
          open
        >
          {responsiveDrawer}
        </Drawer>
      </Box>
    </Box>
  );
}

export default Sidebar;
